import { useState, useEffect, useMemo } from "react";

import {
  getCurrentSabbathSchool,
  getTodayLessonTitle,
  getEffectiveDiffDays,
} from "../lib/sabbathSchool";

const CACHE_KEY = "keves_sabbath_v1";

/* ────────────────────────────────────────────────────────── */
/* localStorage helpers                                       */
/* ────────────────────────────────────────────────────────── */

const lsGet = () => {
  try {
    return JSON.parse(
      localStorage.getItem(CACHE_KEY)
    );
  } catch {
    return null;
  }
};

const lsSet = (value) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(value)
    );
  } catch {}
};

/* ────────────────────────────────────────────────────────── */
/* Precision timer for automatic day/week rollover            */
/* ────────────────────────────────────────────────────────── */

/**
 * Returns a Date representing the next moment when the displayed lesson
 * should change:
 *   - Saturday before noon  → today at 12:00:00.000 PM (pastoral exception)
 *   - Any other day         → tomorrow at 00:00:01.000 AM
 *
 * Adding 1 ms/s margin avoids firing just before the boundary.
 */
function getNextRolloverTime() {
  const now = new Date();
  const isSaturdayMorning = now.getDay() === 6 && now.getHours() < 12;

  if (isSaturdayMorning) {
    // Fire at noon today (Saturday pastoral exception boundary)
    return new Date(
      now.getFullYear(), now.getMonth(), now.getDate(),
      12, 0, 0, 100  // 100 ms past noon to avoid straddling
    );
  }

  // Fire just after midnight tonight
  return new Date(
    now.getFullYear(), now.getMonth(), now.getDate() + 1,
    0, 0, 1, 0  // 1 second past midnight
  );
}

/* ────────────────────────────────────────────────────────── */
/* Hook                                                       */
/* ────────────────────────────────────────────────────────── */

export function useSabbathSchool() {
  // Instant render from cache
  const [plan, setPlan] = useState(() => lsGet());

  const [loading, setLoading] = useState(true);

  // Clock tick — incremented each time the day/week boundary is crossed.
  // Drives useMemo recomputation without re-fetching the plan.
  const [clockTick, setClockTick] = useState(0);

  /* ──────────────────────────────────────────────────────── */
  /* Precision rollover timer                                 */
  /* ──────────────────────────────────────────────────────── */

  useEffect(() => {
    const msUntilRollover = getNextRolloverTime() - Date.now();

    // Schedule a single, precise timeout for the next lesson boundary.
    // When it fires, incrementing clockTick re-triggers this effect, which
    // schedules the following boundary — no polling, no drift.
    const id = setTimeout(() => {
      setClockTick((t) => t + 1);
    }, msUntilRollover);

    return () => clearTimeout(id);
  }, [clockTick]); // Re-arm after each rollover

  /* ──────────────────────────────────────────────────────── */
  /* Fetch + stale-while-revalidate                          */
  /* ──────────────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;

    // If cache exists, show immediately
    if (plan) {
      setLoading(false);
    }

    getCurrentSabbathSchool()
      .then((fresh) => {
        if (cancelled) return;

        if (!fresh) return;

        lsSet(fresh);

        setPlan(fresh);
      })
      .catch((err) => {
        console.error(
          "Sabbath School fetch error:",
          err
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ──────────────────────────────────────────────────────── */
  /* Parse today's lesson — re-runs on plan change OR tick   */
  /* ──────────────────────────────────────────────────────── */

  const lesson = useMemo(() => {
    if (!plan) return null;
    if (!plan?.quarter?.startDate) return null;

    const base = getTodayLessonTitle(plan);

    // Quarter ended or invalid
    if (!base) return null;

    const [y, m, d] = plan.quarter.startDate.split("-").map(Number);
    const startDate = new Date(y, m - 1, d); // local midnight, not UTC

    // Use the same pastoral-exception-aware diff as getTodayLessonTitle so
    // verseText/verseReference always match the displayed lesson week.
    const diffDays = getEffectiveDiffDays(startDate);

    const weekIndex = Math.max(0, Math.floor(diffDays / 7));

    const currentWeek = plan.weeks?.[weekIndex];

    const totalWeeks = plan.weeks?.length ?? 0;

    return {
      quarterTitle: base.quarterTitle,
      weekTitle:    base.weekTitle,
      lessonTitle:  base.lessonTitle,
      weekNumber:   weekIndex + 1,
      totalWeeks,

      // Flexible verse parsing
      verseText:
        currentWeek?.memoryVerse ??
        currentWeek?.verseText ??
        currentWeek?.verse ??
        currentWeek?.memory_verse ??
        null,

      verseReference:
        currentWeek?.memoryVerseRef ??
        currentWeek?.verseReference ??
        currentWeek?.verseRef ??
        currentWeek?.reference ??
        null,
    };
  // clockTick is intentionally included as a re-evaluation signal even though
  // its value isn't read inside the callback — it triggers recomputation when
  // the day/week boundary fires (Saturday noon or midnight on other days).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, clockTick]);

  /* ──────────────────────────────────────────────────────── */
  /* Quarter ended state                                      */
  /* ──────────────────────────────────────────────────────── */

  const quarterEnded = !!plan && lesson === null;

  return {
    lesson,
    loading,
    quarterEnded,
  };
}
